import React from 'react';
import { HiOutlineCloudArrowUp, HiOutlineDocumentCheck, HiOutlineCheckBadge } from "react-icons/hi2";
import useUploadResourceFacade from '../facades/useUploadResourceFacade';

const UploadResources = () => {
  const {
    formData,
    isUploading,
    progress,
    branches,
    materialTypes,
    handleChange,
    handleFileChange,
    handleFormSubmit,
    navigate,
  } = useUploadResourceFacade();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="bg-blue-600 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-blue-100">
            <HiOutlineCloudArrowUp size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Contribute Material</h1>
          <p className="text-slate-500 font-medium mt-2">Your contribution helps hundreds of students learn better.</p>
        </div>

        <form onSubmit={handleFormSubmit} className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/60 p-8 md:p-12 border border-slate-100">
          <div className="space-y-6">

            {/* Branch & Material Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Branch</label>
                <select name="branch" value={formData.branch} onChange={handleChange} required
                  className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all appearance-none">
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Material Type</label>
                <select name="materialType" value={formData.materialType} onChange={handleChange} required
                  className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all appearance-none">
                  <option value="">Select Type</option>
                  {materialTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Subject Name</label>
              <input type="text" name="subject" value={formData.subject} onChange={handleChange} required
                placeholder="e.g. Data Structures / Applied Physics"
                className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all"
              />
            </div>

            {/* Topic */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Topic / Unit Name</label>
              <input type="text" name="topic" value={formData.topic} onChange={handleChange} required
                placeholder="e.g. Unit 1: Array and Linked Lists"
                className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all"
              />
            </div>

            {/* File Upload Zone */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">File Attachment</label>
              <div className="relative group">
                <input type="file" onChange={handleFileChange} required={!formData.downloadURL}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                <div className={`border-2 border-dashed rounded-[2rem] p-8 text-center transition-all ${formData.downloadURL ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50 group-hover:border-blue-300'}`}>
                  {formData.downloadURL ? (
                    <div className="flex flex-col items-center text-green-600">
                      <HiOutlineCheckBadge size={40} className="mb-2" />
                      <p className="text-sm font-black uppercase tracking-widest">Upload Ready ({formData.fileSize} MB)</p>
                    </div>
                  ) : isUploading ? (
                    <div className="space-y-3">
                      <div className="flex justify-between text-xs font-black text-blue-600 uppercase tracking-widest">
                        <span>Uploading...</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-blue-100 h-2 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400">
                      <HiOutlineDocumentCheck size={40} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm font-bold">Tap to select or drag PDF/Docs</p>
                      <p className="text-[10px] uppercase mt-1 font-black tracking-tighter opacity-60">Max size 20MB</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button type="submit" disabled={!formData.downloadURL || isUploading}
              className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black text-sm hover:bg-blue-600 transition-all active:scale-95 disabled:opacity-50 disabled:bg-slate-300 shadow-xl shadow-slate-100">
              Confirm & Post
            </button>
            <button type="button" onClick={() => navigate('/resources')}
              className="px-8 py-4 rounded-2xl font-black text-sm text-slate-400 hover:bg-slate-100 transition-all">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadResources;